import React, {useEffect, useState} from "react";
import {AppState} from "../../RootReducer";
import {useDispatch, useSelector} from "react-redux";
import {Modal} from "react-bootstrap";
import './NotificationsModal.scss';
import {PushSubscription} from "../../http/protocol";
import {getContainer} from "../../ioc/IOCSetup";
import Markdown from "react-markdown";
import {isIOS} from "react-device-detect";
import {ContentModelName} from "../../contenful/types";
import {fetchContent} from "../../contenful/ContentfulActions";
import {t} from "../../i18n"
import {getLoggedIn, isUserInfoLoaded} from "../../selectors/user-selectors";
import {Optional, usePrevious} from "../../user/ui/utils";
import {getStatusForContent} from "../../selectors/content-selectors";
import { PopupStatus } from "../../popup/PopupReducer";
import { LoginStatus } from "../../http/Types";
import { PATH } from "../../onboardingpage/modalview/UserModalController";
import { useLocation } from "react-router";


enum SubscribeStatus {
    NONE,
    SUBSCRIBED,
    NOT_SUBSCRIBED
}

function urlB64ToUint8Array(base64String:string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

const NotificationsModal = () => {

    const dispatch = useDispatch();
    const loggedIn = useSelector((state : AppState) => getLoggedIn(state));
    const userInfoLoaded = useSelector((state : AppState) => isUserInfoLoaded(state))
    const { user } = useSelector((state : AppState) => state.user);
    const popupNotification = useSelector((state : AppState) => state.contentful.popupNotification);
    const settings = useSelector((state : AppState) => state.settings.settings);
    const status = useSelector((state: AppState) => state.popup.popupStatus)
    const location = useLocation();
    const [subscribed, setSubscribed] = useState<SubscribeStatus>(SubscribeStatus.NONE);
    const [publicKey, setPublicKey] = useState<string|undefined>();
    const [show, setShow] = useState<boolean>(false);
    const [denied, setDenied] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const [loggedInOnLoad, setLoggedInOnLoad] = useState<Optional<boolean>>(Optional.empty<boolean>());

    const optOut = () => {
       getContainer().getUserService().updateUserSettings('optOutPushPrompt','true').then(() => {
            setShow(false);
        }).catch(() => {
        });
    }
    
    useEffect(()=>{
        dispatch(fetchContent(ContentModelName.POPUPNOTIFICATION, getContainer().getSettingsService().getActiveLocale().code) as any);

        let localKey = localStorage.getItem("notificationKey");
        if(!localKey) {
            getContainer().getNotificationsService().getPublicKey().then((key:string) => {
                localStorage.setItem("notificationKey", key);
                setPublicKey(key);
            }).catch()
        } else {
            setPublicKey(localKey);
        }

        navigator?.serviceWorker?.ready.then((swRegistration:ServiceWorkerRegistration) => {
            swRegistration.pushManager.getSubscription().then((res) => {
                if (res != null) {
                    setSubscribed(SubscribeStatus.SUBSCRIBED);
                } else {
                    setSubscribed(SubscribeStatus.NOT_SUBSCRIBED);
                }
            });
        }).catch(e => {
            console.log("Unable to get sub Exception ",e);
        });
    },[dispatch])

    let prevLoggedIn = usePrevious(loggedIn);
    let prevUserInfoLoaded = usePrevious(userInfoLoaded);

    useEffect(()=> {
        if(userInfoLoaded == true && prevUserInfoLoaded == false) {
           setLoggedInOnLoad(Optional.of(loggedIn));
        }
    },[userInfoLoaded, loggedIn,prevUserInfoLoaded,prevLoggedIn]);
    useEffect(()=>{
        if(!loggedInOnLoad.isEmpty() && loggedInOnLoad.get() === false && loggedIn == true && prevLoggedIn == false) {
            setReady(true);
        } if(loggedIn == false && prevLoggedIn == true) {
            setReady(false);
            setLoggedInOnLoad(Optional.of(false));
        }
    },[loggedInOnLoad, loggedIn, prevLoggedIn]);

    useEffect(()=> {
        if(subscribed == SubscribeStatus.NOT_SUBSCRIBED && settings?.pushNotificationsEnabled && ready) {
            let loginCount:number =  parseInt(user?.userSettings?.noOfLogin ?? "0");
            let optOutPushPrompt = user?.userSettings?.optOutPushPrompt;
            let settingsLoginRequirement = settings.loginsRequiredForPushPrompt || 1;


            if(settingsLoginRequirement <= 0) {
                settingsLoginRequirement = 1;
            }
            if(!optOutPushPrompt) {
                if(loginCount % settingsLoginRequirement === 0) {
                    setShow(true);
                }
            }
        }
    },[subscribed, settings?.pushNotificationsEnabled, ready, user?.userSettings?.nrOfLogin, settings?.loginsRequiredForPushPrompt])

    const getPermission = () => {
        Notification.requestPermission((status:string) => {
            if(status === 'granted' && publicKey) {
                setShow(false);
                subscribeUser(publicKey);
            } else if(status === "denied") {
                setDenied(true);
            }
        });
    }

    const subscribeUser = (publicKey : string) => {
        const applicationServerKey = urlB64ToUint8Array(publicKey);
        navigator?.serviceWorker?.ready.then((swRegistration:ServiceWorkerRegistration) => {
            swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: applicationServerKey
            }).then((subscription) => {
                if (!subscription) {
                    return;
                }
                let key:any = subscription.getKey ? subscription.getKey('p256dh') : '';
                let auth:any = subscription.getKey ? subscription.getKey('auth') : '';

                // Send subscription to application server
                let sub:PushSubscription = {
                    endpoint:subscription.endpoint,
                    expirationTime:undefined,
                    key: key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key) as any)) : '',
                    auth: auth ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth) as any)) : ''
                };
                getContainer().getNotificationsService().subscribeTo(sub);

            })
            .catch(function() {
            });
        })
    }
    if(!loggedIn ) {
        return <span/>;
    } else {
        if(popupNotification && !isIOS && subscribed == SubscribeStatus.NOT_SUBSCRIBED  && (status === PopupStatus.HIDDEN || status === PopupStatus.IN_ACTIVE) && !new URLSearchParams(location.search).has('type')) {
            return (
                <Modal show={show}
                       onHide={() => {
                           setShow(false);
                       }}
                       aria-labelledby="contained-modal-title-vcenter"
                       centered
                       className="promoteModalDialog"
                       size="lg">
                    <Modal.Header closeButton className="promoteModalHead">
                        <Modal.Title id="contained-modal-title-vcenter" className="promoteModalTitle">
                            {popupNotification.title ? popupNotification.title : ""}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="promoteModalBody">
                        <Markdown className="promoteModalText" children={popupNotification.message ? popupNotification.message : ""} />
                        <div style={{textAlign:"center", marginTop:"1.5rem"}}>
                            {denied && (
                                <div className={"alert alert-danger"}>{t("profile.settings.notifications-blocked-label")}</div>
                            )}
                            <button className="btn btn-cta full-width small" onClick={() => getPermission()}>{popupNotification.approveButtonText ? popupNotification.approveButtonText : ""}</button>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <a className="btn-maybe" onClick={() => optOut()}>{popupNotification.declineButtonText ? popupNotification.declineButtonText : ""}</a>
                    </Modal.Footer>
                </Modal>
            );
        } else {
            return <div/>
        }
    }

}

export default NotificationsModal;

